import { useState, useMemo } from "react";
import { useData } from "../../../context/DataContext.jsx";
import { C, font, mono, card, badge } from "../../../lib/styles.js";
import { fmt, fmtTime, fmtDate, getFirstCaptureDate, getJobDateRange, nextUpcomingSessionDate } from "../../../lib/utils.js";
import {
  JOB_STATUS_LABELS, JOB_STATUS_COLORS,
  SESSION_TYPES, SESSION_TYPE_LABELS, SESSION_TYPE_COLORS,
  DEFAULT_SESSION_TYPE,
} from "../../../lib/constants.js";
import { jobAccess } from "../lib/jobAccess.js";
import { revenueRollup, partsList } from "../lib/revenue.js";
import { revenueYoY } from "../lib/insights.js";
import CrewCardBadge from "./jobcard/CrewCardBadge.jsx";
import { scheduleEntries } from "../../../lib/holds.js";
import { weekSpans } from "../../../lib/scheduleGrid.js";
import { useIsMobile } from "../lib/useIsMobile.js";
import { ChipWithBadge, HoldChip, LegendConflict, LegendHold, HOLD_COLOR, bangStyle, conflictLinesFor, conflictedOwnerDates, holdLine, holdTooltip, jobChipStyle } from "./ScheduleBits.jsx";

const FOLLOW_UP_COLOR = C.amber;
const TODAY_EYEBROW = { flight: "Today's Travel", setup: "Today's Setup", capture: "Today's Shoot", strike: "Today's Strike" };

const parseLocalDate = (s) => { const [y,m,d] = s.split("-").map(Number); return new Date(y, m-1, d); };
const fmtISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const daysUntil = (s) => {
  if (!s) return Infinity;
  const d = parseLocalDate(s); d.setHours(0,0,0,0);
  const now = new Date(); now.setHours(0,0,0,0);
  return Math.round((d - now) / 86400000);
};
const relativeDay = (s) => {
  const n = daysUntil(s);
  if (n === 0) return "Today";
  if (n === 1) return "Tomorrow";
  if (n < 0) return `${Math.abs(n)}d ago`;
  return `${parseLocalDate(s).toLocaleDateString("en-US", { weekday: "short" })} · ${n}d`;
};
// Big right-hand label for an upcoming row. ONE derivation from daysUntil so the
// label, the Today/Tomorrow color keying, and the row highlight can't disagree.
const dueLabel = (iso, n) =>
  !iso ? "Date TBD" : n === 0 ? "Today" : n === 1 ? "Tomorrow" : n > 0 ? `${n} days` : relativeDay(iso);
const getWeekDays = (anchor) => {
  const d = new Date(anchor); const dow = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - ((dow + 6) % 7));
  return Array.from({ length: 14 }, (_, i) => { const dd = new Date(mon); dd.setDate(mon.getDate() + i); return dd; });
};

const ARCHIVE_DAYS = 30;
const isArchived = (j) => {
  if (!j.paid || !j.paidDate) return false;
  const paid = new Date(j.paidDate); const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - ARCHIVE_DAYS);
  return paid < cutoff;
};

const lblStyle = { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: C.muted, marginBottom: 4, fontWeight: 400 };

export default function Dashboard({ navigate, user }) {
  // Holds (2026-09-04, docs/holds/holds-plan.md): the strip reads hold RECORDS
  // plus jobs, never quotes, so admin and crew see one schedule. The role-aware
  // DataProvider exposes `holds` and `role`; older providers and fixtures leave
  // them out, and the role then comes from the user prop as before.
  const { jobs, savedQuotes = [], holds = [], role: dataRole, crewRoster = [], equipmentMap = null } = useData();
  const isMobile = useIsMobile();
  const nav = navigate || (() => {});
  // Role-based access via the shared helper (see jobAccess.js). Phase 1 wiring;
  // money/contact gating on the dashboard lands in phase 2.
  const access = jobAccess(dataRole || user?.role);
  const isCrew = access.isCrew;
  const canSeeMoney = access.canSeeMoney;
  const canSeeContacts = access.canSeeContacts;
  const canSeeAdminDashboardMeta = access.canManageJobs;
  const JSC = JOB_STATUS_COLORS(C);
  const STC = SESSION_TYPE_COLORS(C);

  const [selectedDay, setSelectedDay] = useState(null);

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const todayStr = fmtISO(today);
  const weekDays = useMemo(() => getWeekDays(today), [today]);

  const workingJobs = useMemo(() => jobs.filter(j => !isArchived(j)), [jobs]);

  const allUpcoming = useMemo(() =>
    workingJobs
      .map(j => {
        if (j.status !== "scheduled" && j.status !== "in_progress") return null;
        const sessions = Array.isArray(j.sessions) ? j.sessions : [];
        const hasFutureSession = sessions.length > 0 && sessions.some(s => s?.date && daysUntil(s.date) >= 0);
        const first = getFirstCaptureDate(j);
        if (!hasFutureSession && !(first && daysUntil(first) >= 0)) return null;
        // Sort on the NEXT upcoming session date, not the (possibly past) first capture —
        // so a multi-day job whose first day passed sorts by its real next day (C1).
        return { job: j, sortKey: nextUpcomingSessionDate(j, todayStr) };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.sortKey) - new Date(b.sortKey))
      .map(x => x.job),
  [workingJobs, todayStr]);

  const selectedDayStr = selectedDay !== null ? fmtISO(weekDays[selectedDay]) : null;

  const upcomingJobs = useMemo(() => {
    if (!selectedDayStr) return allUpcoming;
    return allUpcoming.filter(j => {
      const sessions = Array.isArray(j.sessions) ? j.sessions : [];
      if (sessions.length > 0) return sessions.some(s => s?.date === selectedDayStr);
      return getFirstCaptureDate(j) === selectedDayStr;
    });
  }, [allUpcoming, selectedDayStr]);

  const todayJobs = useMemo(() =>
    workingJobs.filter(j => {
      if (j.status === "processing" || j.status === "delivered") return false;
      const sessions = Array.isArray(j.sessions) ? j.sessions : [];
      if (sessions.length > 0) return sessions.some(s => s?.date === todayStr);
      return getFirstCaptureDate(j) === todayStr;
    }),
  [workingJobs, todayStr]);

  const processingJobs = useMemo(() => workingJobs.filter(j => j.status === "processing"), [workingJobs]);

  // F9: scheduled/in-progress jobs with NO resolvable date (no capture date, no dated
  // session). getFirstCaptureDate returns "" for these, so the Upcoming/Today filters
  // drop them and they'd otherwise vanish from the dashboard entirely. Surface them in a
  // small "Needs a date" list so they can be opened and dated — WITHOUT widening Upcoming.
  const needsDateJobs = useMemo(() =>
    workingJobs.filter(j => (j.status === "scheduled" || j.status === "in_progress") && !getFirstCaptureDate(j)),
  [workingJobs]);

  // Follow-ups: live (quoted) quotes with a follow-up date. With a day selected
  // on the strip, narrow to that exact day; otherwise show overdue + the next
  // 7 days, soonest first. (Was a hardcoded [] stub — wired to real quote data
  // as part of the quotes-pipeline rework, 2026-06-10.)
  const allFollowUps = useMemo(() =>
    savedQuotes
      .filter(sq => !sq.draft && String(sq.jobStatus || "quoted").toLowerCase() === "quoted" && sq.followUpDate)
      .sort((a, b) => String(a.followUpDate).localeCompare(String(b.followUpDate))),
  [savedQuotes]);
  const quoteAlerts = useMemo(() => {
    if (selectedDayStr) return allFollowUps.filter(sq => sq.followUpDate === selectedDayStr);
    const horizon = fmtISO(new Date(today.getTime() + 7 * 86400000));
    return allFollowUps.filter(sq => sq.followUpDate <= horizon).slice(0, 8);
  }, [allFollowUps, selectedDayStr, today]);
  const overdueCount = useMemo(() =>
    quoteAlerts.filter(sq => sq.followUpDate <= todayStr).length,
  [quoteAlerts, todayStr]);

  const sessionsByDate = useMemo(() => {
    const map = {};
    const order = (e) => SESSION_TYPES.indexOf(e.session?.sessionType || DEFAULT_SESSION_TYPE);
    allUpcoming.forEach(j => {
      const sessions = Array.isArray(j.sessions) ? j.sessions : [];
      if (sessions.length > 0) {
        sessions.forEach(s => { if (s?.date) { (map[s.date] ||= []).push({ job: j, session: s }); } });
      } else {
        const d = getFirstCaptureDate(j);
        if (d) (map[d] ||= []).push({ job: j, session: null });
      }
    });
    Object.keys(map).forEach(k => map[k].sort((a, b) => order(a) - order(b)));
    return map;
  }, [allUpcoming]);

  const multiDayBars = useMemo(() => {
    const weekStrs = weekDays.map(fmtISO);
    return allUpcoming
      .filter(j => j.multiDay && j.sessions?.length >= 2)
      .map(j => {
        const indices = j.sessions
          .map(s => s?.date).filter(Boolean)
          .map(d => weekStrs.indexOf(d)).filter(i => i !== -1)
          .sort((a, b) => a - b);
        if (!indices.length) return null;
        return { job: j, indices, startIdx: indices[0], endIdx: indices[indices.length - 1] };
      })
      .filter(Boolean);
  }, [allUpcoming, weekDays]);

  // Holds against every live job. Every role reads the client name and subject
  // off the hold record itself (2026-09-05); admins also pass savedQuotes so a
  // record written before those fields still prints its client.
  const schedule = useMemo(() =>
    scheduleEntries(holds, jobs, crewRoster, { equipmentMap, quotes: canSeeAdminDashboardMeta ? savedQuotes : null }),
  [holds, jobs, crewRoster, equipmentMap, savedQuotes, canSeeAdminDashboardMeta]);
  const holdsByDate = useMemo(() => {
    const map = {};
    schedule.entries.forEach(e => { if (e.kind === "hold") (map[e.date] ||= []).push(e); });
    return map;
  }, [schedule]);
  const conflictedDates = useMemo(() => conflictedOwnerDates(schedule), [schedule]);
  // Dashed bars across a hold's consecutive strip days (the 14-day strip is one row).
  const holdBars = useMemo(() =>
    weekSpans(schedule.entries.filter(e => e.kind === "hold"), weekDays.map(fmtISO)),
  [schedule, weekDays]);
  const selectedHolds = selectedDayStr ? (holdsByDate[selectedDayStr] || []) : [];

  const activeJobCount = workingJobs.filter(j => j.status !== "delivered").length;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  // Today's shoot card(s). Defined here so it can render inside the left column UNDER
  // Upcoming jobs (the hero), per the approved K4 mockup, instead of pushing Upcoming
  // down the page.
  const todayBriefing = (todayJobs.length > 0 && selectedDay === null) ? todayJobs.map(j => {
    const todaySession = (Array.isArray(j.sessions) ? j.sessions : []).find(s => s?.date === todayStr) || null;
    const todayType = todaySession?.sessionType || DEFAULT_SESSION_TYPE;
    const todayClr = STC[todayType] || JSC.in_progress;
    const eyebrow = TODAY_EYEBROW[todayType] || TODAY_EYEBROW.capture;
    return (
      <div key={j.id}
        onClick={() => nav("jobs", j.id)}
        style={{
          ...card, padding: 0, overflow: "hidden", marginBottom: 16,
          borderLeft: `3px solid ${todayClr}`, cursor: "pointer",
        }}>
        <div style={{ padding: "16px 20px 0 20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: todayClr, marginBottom: 6, fontWeight: 400 }}>{eyebrow}</div>
            <div style={{ fontSize: 18, fontWeight: 500, marginBottom: 2, letterSpacing: "-0.01em" }}>{j.clientName || "—"}</div>
            <div style={{ fontSize: 12, color: C.muted }}>
              {canSeeContacts && j.contactName ? j.contactName : ""}
              {j.description ? `${canSeeContacts && j.contactName ? " · " : ""}${j.description}` : ""}
            </div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            {canSeeMoney && <div style={{ fontSize: 15, fontWeight: 400 }}>{fmt(j.grossRevenue || 0)}</div>}
            <span style={{ fontFamily: mono, fontSize: 11, color: C.muted }}>{j.jobId || ""}</span>
            <CrewCardBadge job={j} />
          </div>
        </div>
        <div style={{ height: 1, background: C.border, margin: "14px 20px" }} />
        <div style={{ padding: "0 20px 16px", display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1.3fr", gap: isMobile ? 12 : 16 }}>
          <div>
            <div style={lblStyle}>Call Time</div>
            <div style={{ fontSize: 14, fontWeight: 400 }}>{fmtTime(todaySession?.callTime || j.callTime)}</div>
          </div>
          <div>
            <div style={lblStyle}>Scheduled</div>
            <div style={{ fontSize: 14, fontWeight: 400 }}>{fmtTime(j.scheduledTime)}</div>
          </div>
          <div>
            <div style={lblStyle}>Location</div>
            <div style={{ fontSize: 13, fontWeight: 400 }}>{j.locationVenue || j.location || "—"}</div>
            {j.location && j.locationVenue && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{j.location}</div>}
          </div>
          <div>
            <div style={lblStyle}>Equipment</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {(j.equipment || []).length > 0 ? j.equipment.map(eq => (
                <span key={eq} style={{
                  fontSize: 10, padding: "2px 8px", borderRadius: 3,
                  background: `${C.blue}1a`, color: C.blue, border: `1px solid ${C.blue}38`,
                }}>{eq}</span>
              )) : <span style={{ fontSize: 11, color: C.muted }}>—</span>}
            </div>
          </div>
        </div>
        {(j.crew || j.transport) && (
          <>
            <div style={{ height: 1, background: C.border, margin: "0 20px" }} />
            <div style={{ padding: "10px 20px", display: "flex", gap: 22, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
              {j.crew && <span><span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 6 }}>Crew</span>{j.crew}</span>}
              {j.transport && <span><span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 6 }}>Transport</span>{j.transport}</span>}
            </div>
          </>
        )}
      </div>
    );
  }) : null;

  return (
    <div style={{ animation: "fadeUp 0.3s ease" }}>

      {/* Header */}
      {/* Identity band: greeting left, the day's numbers as quiet tiles at the
          right — the same treatment the Quotes pipeline figure gets, so the two
          pages read as one system. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 2, letterSpacing: "-0.01em" }}>{greeting}</div>
          <div style={{ fontSize: 12, color: C.muted }}>
            {today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { label: "Active", value: activeJobCount, color: C.blue },
            { label: "Processing", value: processingJobs.length, color: JSC.processing },
            ...(canSeeMoney ? [{ label: "Follow-ups", value: overdueCount, color: overdueCount > 0 ? C.amber : C.muted }] : []),
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "8px 15px",
              minWidth: 92,
            }}>
              <div style={{ fontSize: 18, fontWeight: 500, color, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
              <div style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.1em", color: C.muted, marginTop: 3 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar strip — demoted to a thin secondary band; Upcoming jobs is the hero below. */}
      <div data-testid="strip" style={{ ...card, padding: "7px 12px 9px", marginBottom: 14, overflowX: isMobile ? "auto" : "visible", WebkitOverflowScrolling: "touch" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
          <span style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: C.muted }}>Schedule · next 2 weeks</span>
          {isMobile && <span style={{ fontSize: 9, color: C.muted }}>swipe →</span>}
        </div>
        <div style={{ position: "relative", minWidth: isMobile ? 940 : undefined }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 0, position: "relative", zIndex: 1 }}>
            {weekDays.map((d, i) => {
              const iso = fmtISO(d);
              const isToday = iso === todayStr;
              const isSelected = selectedDay === i;
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const entries = sessionsByDate[iso] || [];
              const dayHolds = holdsByDate[iso] || [];
              const chipCount = entries.length + dayHolds.length;
              return (
                <div key={i} style={{ display: "flex", alignItems: "center" }}>
                  {i === 7 && <div style={{ width: 1, height: 44, background: C.border, margin: "0 6px", alignSelf: "center" }} />}
                  <div
                    data-testid="strip-day" data-date={iso}
                    onClick={() => setSelectedDay(isSelected ? null : i)}
                    style={{
                      flex: "0 0 auto", width: 66, padding: "6px 0 4px",
                      display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
                      cursor: "pointer", borderRadius: 5,
                      background: isSelected ? `${C.blue}14` : "transparent",
                      border: isSelected ? `1px solid ${C.blue}38` : "1px solid transparent",
                      transition: "background 0.15s ease",
                    }}
                  >
                    <div style={{
                      fontSize: 9, textTransform: "uppercase", letterSpacing: "0.1em",
                      color: isToday ? C.amber : isWeekend ? `${C.muted}8c` : C.muted, fontWeight: 400,
                    }}>
                      {d.toLocaleDateString("en-US", { weekday: "short" })}
                    </div>
                    <div style={{
                      fontSize: 15, fontWeight: isToday ? 500 : 400,
                      color: isToday ? C.amber : isWeekend ? `${C.muted}c0` : C.text,
                      width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center",
                      borderRadius: 14, background: isToday ? `${C.amber}26` : "transparent",
                    }}>{d.getDate()}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", minHeight: 14 }}>
                      {entries.slice(0, 2).map(({ job, session }, ei) => {
                        const type = session?.sessionType || DEFAULT_SESSION_TYPE;
                        const clr = STC[type] || JSC[job.status] || C.muted;
                        const faded = type === "setup" || type === "strike";
                        const label = job.jobId ? job.jobId.split("_")[0] : "—";
                        const typeLabel = SESSION_TYPE_LABELS[type] || type;
                        return (
                          <ChipWithBadge key={session?.id || `${job.id}_${ei}`} conflicted={conflictedDates.has(`job:${job.id}|${iso}`)}>
                            <div data-testid="cal-chip-job"
                              title={`${typeLabel} · ${job.clientName || job.jobId || ""}`}
                              style={jobChipStyle(clr, faded)}>{label}</div>
                          </ChipWithBadge>
                        );
                      })}
                      {/* Hold chips take the slots the job chips leave; the 2-chip cap and the count cover both kinds. */}
                      {dayHolds.slice(0, Math.max(0, 2 - entries.length)).map(e => (
                        <HoldChip key={e.key} entry={e} conflicted={conflictedDates.has(`${e.owner}|${iso}`)}
                          title={holdTooltip(e)} />
                      ))}
                      {chipCount > 2 && <div style={{ fontSize: 7.5, color: C.muted }}>+{chipCount - 2}</div>}
                      {chipCount === 0 && <div style={{ height: 11 }} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {multiDayBars.map((bar, bi) => {
            // Must track the day-cell width + week separator rendered above.
            const cellW = 66;
            const sepW = 13;
            const offset = (idx) => idx >= 7 ? sepW : 0;
            const left = bar.startIdx * cellW + offset(bar.startIdx);
            const width = (bar.endIdx - bar.startIdx + 1) * cellW + (offset(bar.endIdx) - offset(bar.startIdx));
            const clr = JSC[bar.job.status] || C.blue;
            return (
              <div key={`bar-${bi}`}
                title={bar.job.clientName || bar.job.jobId}
                onClick={() => nav("jobs", bar.job.id)}
                style={{
                  position: "absolute", top: 2, bottom: 2, left, width,
                  background: `${clr}1a`, border: `2px solid ${clr}59`,
                  borderRadius: 6, cursor: "pointer", zIndex: 2, pointerEvents: "auto",
                }} />
            );
          })}
          {holdBars.map((bar) => {
            // Same geometry as the job bars above: 66px cells, a 13px separator before day 8.
            const cellW = 66;
            const sepW = 13;
            const offset = (idx) => idx >= 7 ? sepW : 0;
            const left = bar.startCol * cellW + offset(bar.startCol);
            const width = (bar.endCol - bar.startCol + 1) * cellW + (offset(bar.endCol) - offset(bar.startCol));
            return (
              <div key={`${bar.owner}-${bar.startCol}`} data-testid="strip-hold-bar"
                title={holdTooltip(bar, bar.dates)}
                style={{
                  position: "absolute", top: 2, bottom: 2, left, width,
                  background: "transparent", border: `2px dashed ${HOLD_COLOR}59`,
                  borderRadius: 6, zIndex: 2, pointerEvents: "none",
                }} />
            );
          })}
        </div>
        {canSeeAdminDashboardMeta && (
          <div data-testid="strip-legend" style={{ display: "flex", gap: 14, justifyContent: "flex-end", marginTop: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 18, height: 6, borderRadius: 2, background: `${C.blue}40`, border: `1px solid ${C.blue}80` }} />
              <span style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Multi-day</span>
            </div>
            <LegendHold />
            <LegendConflict />
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 5, height: 5, background: FOLLOW_UP_COLOR, transform: "rotate(45deg)", borderRadius: 1 }} />
              <span style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em" }}>Follow-up</span>
            </div>
          </div>
        )}
      </div>

      {/* Main content — Upcoming jobs is the hero; Today's briefing renders under it. */}
      {isCrew ? (
        <>
          <UpcomingJobsList jobs={upcomingJobs} holds={selectedHolds} schedule={schedule} equipmentMap={equipmentMap}
            isCrew={true} canSeeMoney={canSeeMoney} canUseQuotes={access.canUseQuotes} JSC={JSC} STC={STC}
            selectedDate={selectedDayStr} onClear={() => setSelectedDay(null)} todayStr={todayStr} nav={nav} />
          {todayBriefing}
          <ProcessingList jobs={processingJobs} JSC={JSC} nav={nav} />
        </>
      ) : (
        // minmax(0, …) tracks: a plain 1fr lets a nowrap line (e.g. a follow-up
        // row's contact · summary) blow the column past a 320px viewport —
        // grid items won't shrink below min-content without an explicit 0 floor.
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.4fr) minmax(0, 1fr)", gap: 16, marginBottom: 16 }}>
          <div>
            <UpcomingJobsList jobs={upcomingJobs} holds={selectedHolds} schedule={schedule} equipmentMap={equipmentMap}
              isCrew={false} canSeeMoney={canSeeMoney} canUseQuotes={access.canUseQuotes} JSC={JSC} STC={STC}
              selectedDate={selectedDayStr} onClear={() => setSelectedDay(null)} todayStr={todayStr} nav={nav} />
            {todayBriefing}
          </div>
          <div>
            {canSeeMoney && <FollowUpsList quotes={quoteAlerts} overdueCount={overdueCount}
              selectedDay={selectedDay} weekDays={weekDays} nav={nav} />}
            <ProcessingList jobs={processingJobs} JSC={JSC} nav={nav} />
          </div>
        </div>
      )}

      {/* Needs a date (F9) — scheduled/in-progress jobs with no resolvable date, which
          otherwise vanish from Upcoming/Today. Unobtrusive; renders only when present. */}
      <NeedsDateList jobs={needsDateJobs} nav={nav} />

      {/* Revenue snapshot (admin only) */}
      {canSeeMoney && <RevenueBar jobs={jobs} quotes={savedQuotes} isMobile={isMobile} />}
    </div>
  );
}

function NeedsDateList({ jobs, nav }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 16, borderColor: `${C.amber}59` }}>
      <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, background: `${C.amber}10` }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.amber, flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>Needs a date</span>
        <span style={{ fontSize: 11, color: C.muted }}>· {jobs.length} scheduled {jobs.length === 1 ? "job has" : "jobs have"} no capture date — set one so they show up</span>
      </div>
      <div style={{ height: 1, background: `${C.amber}40` }} />
      {jobs.map((j, i) => (
        <div key={j.id}>
          <div onClick={() => nav("jobs", j.id)} style={{
            padding: "9px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
            gap: 10, cursor: "pointer",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 400 }}>{j.clientName || "—"}</span>
              <span style={{ fontFamily: mono, fontSize: 10, color: C.muted }}>{j.jobId || ""}</span>
            </div>
            <span style={{ fontSize: 11, color: C.amber, whiteSpace: "nowrap" }}>Set a date →</span>
          </div>
          {i < jobs.length - 1 && <div style={{ height: 1, background: C.border, margin: "0 16px" }} />}
        </div>
      ))}
    </div>
  );
}

// Distinct session types on a job, in capture order. Single-day jobs (no sessions)
// default to a "capture" chip so every row reads consistently.
function sessionTypeChips(j) {
  const sess = Array.isArray(j.sessions) ? j.sessions : [];
  const types = [];
  sess.forEach(s => { const t = s?.sessionType; if (t && !types.includes(t)) types.push(t); });
  if (types.length === 0) types.push(DEFAULT_SESSION_TYPE);
  return types;
}

// Left date block for an upcoming-job row: big day number + short month, or "TBD".
function DateBlock({ iso, isToday }) {
  if (!iso) {
    return (
      <div style={DB.block}>
        <div style={{ ...DB.day, fontSize: 12, color: C.muted }}>TBD</div>
      </div>
    );
  }
  const dt = parseLocalDate(iso);
  const month = dt.toLocaleDateString("en-US", { month: "short" });
  return (
    <div style={DB.block}>
      <div style={{ ...DB.day, color: isToday ? C.green : C.text }}>{dt.getDate()}</div>
      <div style={DB.month}>{month}</div>
    </div>
  );
}

const DB = {
  block: { width: 50, textAlign: "center", flexShrink: 0 },
  day: { fontSize: 19, fontWeight: 500, lineHeight: 1 },
  month: { fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.08em", color: C.muted, marginTop: 3 },
};

// The day panel. Exported for the Schedule month view, which lists the same rows
// for its selected day. `holds` are the hold entries on the selected date: one
// line each (label, client, equipment, crew, for every role) plus every
// conflict sentence. Money stays behind canSeeMoney on the job rows.
export function UpcomingJobsList({ jobs, holds = [], schedule = null, equipmentMap = null, isCrew, canSeeMoney, canUseQuotes = false, JSC, STC, selectedDate = null, onClear, todayStr, nav }) {
  return (
    <div data-testid="day-panel" style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 12 }}>
      <div style={{ padding: "14px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 14, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
          {selectedDate ? (
            <>
              {parseLocalDate(selectedDate).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
              <button onClick={onClear} style={{
                padding: "2px 8px", background: "transparent", color: C.muted,
                border: `1px solid ${C.border}`, borderRadius: 3, fontSize: 9,
                cursor: "pointer", fontFamily: font,
              }}>✕ Clear</button>
            </>
          ) : (
            <>Upcoming jobs<span style={{ fontSize: 11, color: C.muted, fontWeight: 400, marginLeft: 6 }}>· next 2 weeks</span></>
          )}
        </div>
        <button onClick={() => nav("jobs")} style={{
          padding: "4px 12px", background: "transparent", color: C.blue,
          border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 11,
          cursor: "pointer", fontFamily: font, fontWeight: 400,
        }}>All jobs →</button>
      </div>
      <div style={{ height: 1, background: C.border }} />
      {jobs.length === 0 && holds.length === 0 ? (
        <div style={{ padding: "32px 18px", textAlign: "center", color: C.muted, fontSize: 12 }}>
          No jobs {selectedDate ? "on this day" : "upcoming"}
        </div>
      ) : jobs.map((j, i) => {
        // When a calendar day is selected, the row describes THAT day (the list is
        // already filtered to jobs with a session on it) — otherwise anchor on the
        // job's NEXT upcoming session date (C1: a multi-day job whose first day
        // passed anchors on its real next day, so date block / days-until / Today
        // highlight / sort all agree).
        const nextDate = selectedDate || nextUpcomingSessionDate(j, todayStr);
        const range = getJobDateRange(j);
        const isMulti = j.multiDay && range.start && range.end && range.start !== range.end;
        const n = daysUntil(nextDate);
        const isToday = n === 0;
        const clr = JSC[j.status] || C.muted;
        const chips = sessionTypeChips(j);
        const dueBig = dueLabel(nextDate, n);
        return (
          <div key={j.id}>
            <div onClick={() => nav("jobs", j.id)} style={{
              display: "grid", gridTemplateColumns: "50px 1fr auto", gap: 14, alignItems: "center",
              padding: "13px 18px",
              background: isToday ? `${JSC.in_progress}0f` : "transparent",
              cursor: "pointer", transition: "background 0.15s ease",
            }}>
              <DateBlock iso={nextDate || range.start} isToday={isToday} />
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{j.clientName || "—"}</span>
                  <span style={badge(clr)}>{JOB_STATUS_LABELS[j.status]}</span>
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>
                  {j.locationVenue || j.location || "Location TBD"}
                  {j.scheduledTime && <span> · {fmtTime(j.scheduledTime)}</span>}
                  {canSeeMoney && <span> · {fmt(j.grossRevenue || 0)}</span>}
                  {j.jobId && <span style={{ fontFamily: mono, fontSize: 10.5 }}> · {j.jobId}</span>}
                  <CrewCardBadge job={j} />
                </div>
                <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
                  {chips.map(t => {
                    const cc = STC[t] || C.muted;
                    return (
                      <span key={t} style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 4,
                        background: `${cc}1f`, color: cc,
                      }}>{SESSION_TYPE_LABELS[t] || t}</span>
                    );
                  })}
                  {/* Equipment shows for EVERY role — crew prep their rig load off this
                      list (the old dashboard showed crew the gear; don't admin-gate it). */}
                  {j.equipment?.length > 0 && (
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: `${C.blue}14`, color: C.blue }}>
                      {j.equipment[0]}{j.equipment.length > 1 ? ` +${j.equipment.length - 1}` : ""}
                    </span>
                  )}
                </div>
              </div>
              {/* nowrap only on the short due-label; the multi-day range line may wrap.
                  A nowrap "Jun 15 → Jun 18" makes the row's min-content wider than a
                  320px viewport and forces the whole page to scroll sideways. */}
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", color: isToday ? C.green : n === 1 ? C.blue : C.text }}>{dueBig}</div>
                {/* Multi-day jobs show their full span — the end date matters for
                    booking around an install (e.g. "Jun 15 → Jun 18"). */}
                {isMulti && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{fmtDate(range.start)} → {fmtDate(range.end)}</div>}
              </div>
            </div>
            {(i < jobs.length - 1 || holds.length > 0) && <div style={{ height: 1, background: C.border, margin: "0 18px" }} />}
          </div>
        );
      })}
      {holds.map((e, i) => (
        <HoldRow key={e.key} entry={e} line={holdLine(e, { equipmentMap })}
          conflicts={conflictLinesFor(e, schedule, { equipmentMap })}
          JSC={JSC} STC={STC} last={i === holds.length - 1}
          onClick={canUseQuotes ? () => nav("quotes") : undefined} />
      ))}
    </div>
  );
}

// One hold on the day panel: its line (with the client, for every role), the
// day's session kinds, and each conflict as a complete sentence. No money and
// no location on this row; admins can open Quotes from it.
function HoldRow({ entry, line, conflicts, JSC, STC, last, onClick }) {
  const n = daysUntil(entry.date);
  const isToday = n === 0;
  return (
    <div data-testid="hold-row" data-key={entry.key}>
      <div onClick={onClick} style={{
        display: "grid", gridTemplateColumns: "50px 1fr auto", gap: 14, alignItems: "center",
        padding: "13px 18px",
        background: isToday ? `${JSC.in_progress}0f` : "transparent",
        cursor: onClick ? "pointer" : "default", transition: "background 0.15s ease",
      }}>
        <DateBlock iso={entry.date} isToday={isToday} />
        <div style={{ minWidth: 0 }}>
          <div data-testid="hold-line" style={{ fontSize: 14, fontWeight: 500, overflowWrap: "anywhere" }}>{line}</div>
          <div style={{ display: "flex", gap: 5, marginTop: 6, flexWrap: "wrap" }}>
            {entry.kinds.map(t => {
              const cc = STC[t] || C.muted;
              return (
                <span key={t} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: `${cc}1f`, color: cc }}>{SESSION_TYPE_LABELS[t] || t}</span>
              );
            })}
            {entry.dayType === "partial" && (
              <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: C.surfaceAlt, color: C.muted }}>Partial day</span>
            )}
          </div>
          {conflicts.map(sentence => (
            <div key={sentence} data-testid="hold-conflict" style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 6, fontSize: 11.5, color: C.capture, lineHeight: 1.4 }}>
              <span style={{ ...bangStyle, marginTop: 3 }}>!</span><span>{sentence}</span>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", color: isToday ? C.green : n === 1 ? C.blue : C.text }}>{dueLabel(entry.date, n)}</div>
        </div>
      </div>
      {!last && <div style={{ height: 1, background: C.border, margin: "0 18px" }} />}
    </div>
  );
}

function FollowUpsList({ quotes, overdueCount, selectedDay, weekDays, nav }) {
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden", marginBottom: 12 }}>
      <div style={{ padding: "14px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 400, display: "flex", alignItems: "center", gap: 8 }}>
          Follow-ups
          {selectedDay !== null ? (
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>
              {weekDays[selectedDay].toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          ) : overdueCount > 0 && (
            <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 18, height: 18, borderRadius: 9, fontSize: 10, fontWeight: 500,
              background: `${C.amber}38`, color: C.amber, border: `1px solid ${C.amber}59`,
            }}>{overdueCount}</span>
          )}
        </div>
        <button onClick={() => nav("quotes")} style={{
          padding: "4px 12px", background: "transparent", color: C.muted,
          border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 10,
          cursor: "pointer", fontFamily: font, fontWeight: 400,
        }}>Quotes →</button>
      </div>
      <div style={{ height: 1, background: C.border }} />
      {quotes.length === 0 ? (
        <div style={{ padding: "24px 18px", textAlign: "center", color: C.muted, fontSize: 12 }}>
          No follow-ups {selectedDay !== null ? "on this day" : "due"}
        </div>
      ) : quotes.map((q, i) => {
        const n = daysUntil(q.followUpDate);
        const overdue = n <= 0;
        const isToday = n === 0;
        return (
          <div key={q.id}>
            <div onClick={() => nav("quotes")} style={{
              padding: "10px 18px",
              background: overdue ? `${C.amber}0a` : "transparent",
              cursor: "pointer", transition: "background 0.15s ease",
            }}>
              {/* minWidth: 0 on the shrinking sides — flex items default to
                  min-width:auto, so without it long names/summaries push the
                  card past a 320px viewport instead of truncating. */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2, gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <div style={{ width: 5, height: 5, background: overdue ? C.danger : FOLLOW_UP_COLOR, transform: "rotate(45deg)", borderRadius: 1, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.clientName || "—"}</span>
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 400, flexShrink: 0,
                  color: overdue ? (isToday ? C.amber : C.danger) : C.muted,
                }}>
                  {isToday ? "Today" : n < 0 ? `${Math.abs(n)}d overdue` : `In ${n}d`}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingLeft: 11, gap: 10 }}>
                <div style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                  {q.contactName || ""}
                  {q.summary ? (q.contactName ? " · " : "") + (q.summary.length > 40 ? q.summary.slice(0, 40) + "…" : q.summary) : ""}
                </div>
                <div style={{ fontSize: 12, fontWeight: 400, flexShrink: 0 }}>{fmt(q.total || 0)}</div>
              </div>
            </div>
            {i < quotes.length - 1 && <div style={{ height: 1, background: C.border, margin: "0 18px" }} />}
          </div>
        );
      })}
    </div>
  );
}

function ProcessingList({ jobs, JSC, nav }) {
  if (jobs.length === 0) return null;
  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 13, fontWeight: 400 }}>Processing</div>
        <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>Delivery pipeline</span>
      </div>
      <div style={{ height: 1, background: C.border }} />
      {jobs.map((j, i) => {
        const left = j.deliveryDate ? daysUntil(j.deliveryDate) : null;
        const urgent = left !== null && left <= 2;
        return (
          <div key={j.id}>
            <div onClick={() => nav("jobs", j.id)} style={{ padding: "12px 18px", cursor: "pointer", transition: "background 0.15s ease" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 400 }}>{j.clientName || "—"}</span>
                  <span style={badge(JSC.processing)}>Processing</span>
                </div>
                {left !== null && <span style={{ fontSize: 10, fontWeight: 400, color: urgent ? C.amber : C.muted }}>
                  {left === 0 ? "Due today" : left < 0 ? `${Math.abs(left)}d late` : `${left}d left`}
                </span>}
              </div>
              <div style={{ fontSize: 11, color: C.muted }}>
                <span style={{ fontFamily: mono, fontSize: 10 }}>{j.jobId}</span>
                {j.deliverableType && <span> · {j.deliverableType}</span>}
                {j.modeler && <span> · {j.modeler}</span>}
              </div>
            </div>
            {i < jobs.length - 1 && <div style={{ height: 1, background: C.border, margin: "0 18px" }} />}
          </div>
        );
      })}
    </div>
  );
}

function RevenueBar({ jobs, quotes, isMobile }) {
  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();

  // Rollup (2026-08-25): change orders fold into their parent project, so a CO
  // child job is never its own money row here and an unbooked CO quote is no
  // longer invisible. revenueRollup is the same iteration Insights and the
  // Jobs list use — the YTD figure reconciles with both.
  const rollups = revenueRollup(jobs, quotes);

  // Revenue uses getFirstCaptureDate so multi-session jobs anchor on their actual first capture.
  const delivered = rollups.filter(r => r.job.status === "delivered" || r.job.invoiced || r.job.paid);
  const inMonth = (j) => { const d = getFirstCaptureDate(j); if (!d) return false; const dt = parseLocalDate(d); return dt.getMonth() === thisMonth && dt.getFullYear() === thisYear; };

  const monthDelivered = delivered.filter(r => inMonth(r.job));
  const monthRevenue = monthDelivered.reduce((sum, r) => sum + r.parts.total, 0);

  const pipeline = rollups.filter(r => !isArchived(r.job) && r.job.status !== "delivered");
  const pipelineValue = pipeline.reduce((sum, r) => sum + r.parts.total, 0);

  // Outstanding is a PART-level tier, not a job filter: a change order invoices
  // on its own schedule, so a paid parent can still carry an outstanding CO.
  const outstandingByProject = rollups
    .map(r => partsList(r.parts).filter(p => p.tier === "outstanding").reduce((s, p) => s + p.amount, 0))
    .filter(v => v > 0);
  const outstandingValue = outstandingByProject.reduce((s, v) => s + v, 0);
  const outstandingCount = outstandingByProject.length;

  // YTD is Insights' totalCurrent, taken from the same function — not a
  // reimplementation — so the two can never drift (gpt F1: the old
  // delivered-only subset silently dropped scheduled current-year work that
  // Insights and the Jobs list both count). Total AR for the year, all tiers.
  const ytd = revenueYoY(jobs, quotes, thisYear).totalCurrent;

  return (
    <div style={{ ...card, display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr", gap: isMobile ? 12 : 16, padding: 16 }}>
      {[
        { label: "This Month", value: fmt(monthRevenue), sub: `${monthDelivered.length} delivered` },
        { label: "Pipeline", value: fmt(pipelineValue), sub: `${pipeline.length} active` },
        { label: "Outstanding", value: fmt(outstandingValue), sub: `${outstandingCount} unpaid` },
        { label: "YTD", value: fmt(ytd), sub: `${thisYear} total AR` },
      ].map(({ label, value, sub }) => (
        <div key={label}>
          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.12em", color: C.muted, marginBottom: 5 }}>{label}</div>
          <div style={{ fontSize: 17, fontWeight: 500 }}>{value}</div>
          <div style={{ fontSize: 10, color: C.muted }}>{sub}</div>
        </div>
      ))}
    </div>
  );
}
